import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import BookingLayout from 'pages/booking/BookingLayout';
import { useApi } from 'api/api-context';
import useLang from 'lang';
import { BOOKING_LOGIN_PATH } from 'routes/paths';
import IconArrowLeft from 'assets/arrow-narrow-left.svg';
import 'pages/booking/booking-auth-form.css';

/** Request and complete a forgotten-password reset. */
export default function BookingForgotPassword() {
  const { request } = useApi();
  const { _t } = useLang();
  const [email, setEmail] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  /** Request a password-reset code without disclosing account existence. */
  const requestReset = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    try {
      const response = await request<{ token?: string | null }>({
        method: 'POST',
        url: '/reset-password/request',
        data: { email },
      });
      setToken(response.token || 'unavailable');
    } catch {
      setToken('unavailable');
    }
  };

  /** Set the new password after the emailed code is verified. */
  const reset = async (event: FormEvent) => {
    event.preventDefault();
    if (!token || token === 'unavailable') return;
    try {
      await request({ method: 'POST', url: '/change-password', data: { token, code, password } });
      setDone(true);
    } catch {
      setError(_t('The code is invalid or expired.'));
    }
  };

  return (
    <BookingLayout>
      <main className="booking-auth-page">
        <section className="booking-auth-card booking-password-reset" aria-labelledby="booking-reset-title">
          <h1 id="booking-reset-title">{_t('Reset password')}</h1>
          {done ? (
            <div className="booking-auth-notice booking-auth-notice--success" role="status">
              {_t('Your password has been changed.')}
            </div>
          ) : !token ? (
            <form onSubmit={requestReset}>
              <label htmlFor="booking-reset-email">
                <span>{_t('Email')}</span>
                <input
                  id="booking-reset-email"
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={_t('your@email.com')}
                  autoComplete="email"
                />
              </label>
              <button type="submit">{_t('Send reset code')}</button>
            </form>
          ) : token === 'unavailable' ? (
            <div className="booking-auth-notice" role="status">
              {_t('If the account can be reset, an email has been sent.')}
            </div>
          ) : (
            <form onSubmit={reset}>
              <p className="booking-auth-copy">{_t('Enter the code from your email.')}</p>
              <label htmlFor="booking-reset-code">
                <span>{_t('Code')}</span>
                <input
                  id="booking-reset-code"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                />
              </label>
              <label htmlFor="booking-reset-password">
                <span>{_t('New password')}</span>
                <input
                  id="booking-reset-password"
                  required
                  minLength={8}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </label>
              {error && (
                <p className="booking-auth-error" role="alert">
                  {error}
                </p>
              )}
              <button type="submit">{_t('Change password')}</button>
            </form>
          )}
          <div className="booking-auth-footer">
            <Link className="booking-auth-back-link" to={BOOKING_LOGIN_PATH.original}>
              <img src={IconArrowLeft} width={20} height={20} alt="" />
              {_t('Back to login')}
            </Link>
          </div>
        </section>
      </main>
    </BookingLayout>
  );
}
